import React, { useEffect, useState } from 'react';

import { Typography } from '@sunwisesoftware/sunwise-ui';
import { useApiIsLoaded } from '@vis.gl/react-google-maps';
import { isEqual } from 'lodash';
import PropTypes from 'prop-types';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { POLYGON_OBSTACLE } from 'common/components/segment/constants';
import useDebounce from 'common/hooks/useDebounce';
import {
    getMinMaxCols,
    getMinMaxRows,
} from 'common/utils/helpers/systemsValues';

import { getNewModules } from '../../../helpers';

const getModulesQuantity = async ({ google, map, segments, values }) => {
    const {
        algorithm,
        azimuth,
        building_heading,
        ceil_tilt,
        col_spacing,
        frame_col_spacing,
        frame_cols_number,
        frame_row_spacing,
        frame_rows_number,
        height,
        is_custom_module,
        orientation,
        panel,
        polygon,
        row_spacing,
        safe_zone,
        sloof_type,
        solar_module,
        system_uid,
        tilt,
    } = values;

    let selectedModule = values.selectedModule;

    if (is_custom_module) {
        selectedModule = panel;
    }

    if (!selectedModule) return 0;

    const obstacles = segments.filter(
        (segment) => segment.type === POLYGON_OBSTACLE,
    );

    const [minCols, maxCols] = getMinMaxCols(system_uid, panel.width);
    const [minRows, maxRows] = getMinMaxRows(system_uid);

    const data = {
        algorithm,
        assigned: 0,
        azimuth,
        building_heading,
        ceil_tilt,
        col_spacing,
        frame_col_spacing,
        frame_cols_number,
        frame_row_spacing,
        frame_rows_number,
        height,
        maxCols,
        maxRows,
        minCols,
        minRows,
        orientation,
        polygon: JSON.stringify(polygon),
        quantity: Math.Infinity,
        row_spacing,
        safe_zone,
        sloof_type: sloof_type || null,
        solar_module,
        tilt,
    };

    const { boxes } = getNewModules({
        algorithm,
        building_heading,
        data,
        google,
        map,
        module: selectedModule,
        obstacles,
        sloof_type,
    });

    return boxes.length;
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getWatchedValues = (values) => {
    const [
        algorithm,
        azimuth,
        building_heading,
        ceil_tilt,
        col_spacing,
        frame_col_spacing,
        frame_cols_number,
        frame_row_spacing,
        frame_rows_number,
        height,
        is_custom_module,
        orientation,
        panel,
        polygon,
        row_spacing,
        safe_zone,
        selectedModule,
        sloof_type,
        solar_module,
        system_uid,
        tilt,
    ] = values;

    return {
        algorithm,
        azimuth,
        building_heading,
        ceil_tilt,
        col_spacing,
        frame_col_spacing,
        frame_cols_number,
        frame_row_spacing,
        frame_rows_number,
        height,
        is_custom_module,
        orientation,
        panel,
        polygon,
        row_spacing,
        safe_zone,
        selectedModule,
        sloof_type,
        solar_module,
        system_uid,
        tilt,
    };
};

const MaxPanelLabel = ({ control, map, segments }) => {
    const { t } = useTranslation();
    const [google, setGoogle] = useState(null);
    const apiIsLoaded = useApiIsLoaded();

    const values = useWatch({
        control,
        name: [
            'algorithm',
            'azimuth',
            'building_heading',
            'ceil_tilt',
            'col_spacing',
            'frame_col_spacing',
            'frame_cols_number',
            'frame_row_spacing',
            'frame_rows_number',
            'height',
            'is_custom_module',
            'orientation',
            'panel',
            'polygon',
            'row_spacing',
            'safe_zone',
            'selectedModule',
            'sloof_type',
            'solar_module',
            'system_uid',
            'tilt',
        ],
    });
    const watchedValues = getWatchedValues(values);
    const [numPanels, setNumPanels] = useState(0);
    const [previousValues, setPreviousValues] = useState(watchedValues);
    const debouncedValue = useDebounce(values, 250);

    const handler = ({ sleepTime = 0 } = {}) => {
        const getNumPanels = async () => {
            if (sleepTime) await sleep(sleepTime);
            return getModulesQuantity({
                google,
                map,
                segments,
                values: watchedValues,
            });
        };

        getNumPanels().then((response) => {
            setNumPanels(response);
            setPreviousValues(watchedValues);
        });
    };

    useEffect(() => {
        if (!apiIsLoaded) return;
        setGoogle(window.google);
    }, [apiIsLoaded]);

    useEffect(() => {
        handler({ sleepTime: 1 });
    }, []);

    useEffect(() => {
        if (isEqual(previousValues, watchedValues)) return;
        handler();
    }, [debouncedValue]);

    return (
        <Typography variant="caption">
            {t('Maximum number of panels')}: {numPanels} u
        </Typography>
    );
};

MaxPanelLabel.propTypes = {
    control: PropTypes.object,
    map: PropTypes.object,
    segments: PropTypes.array,
};

export default MaxPanelLabel;
